import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Store, MapPin, Phone, Mail, User, Calendar, Shield,
  CheckCircle, XCircle, Ban, Clock, Star, FileText, TrendingUp,
  Edit, Loader, AlertTriangle, Package, DollarSign, Building,
  Globe, Hash, Archive, ChevronRight, Image as ImageIcon
} from 'lucide-react';
import adminService, {
  BusinessWithDetails,
  KYCDocument,
  BusinessStatus,
  Business
} from '../../services/admin/adminService';
import toast from 'react-hot-toast';
import { getImageUrl } from '../../utils/imageHelper';
import { formatDate } from '../../utils/dateUtils';

const BusinessDetails: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();

  const [business, setBusiness] = useState<BusinessWithDetails | null>(null);
  const [kycDocuments, setKycDocuments] = useState<KYCDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('Business Info');
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [failedPhotoIds, setFailedPhotoIds] = useState<number[]>([]);

  // Action modals
  const [approveModal, setApproveModal] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [suspendModal, setSuspendModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [suspensionReason, setSuspensionReason] = useState('');

  useEffect(() => {
    if (businessId) {
      fetchBusinessDetails();
    }
  }, [businessId]);

  const fetchBusinessDetails = async () => {
    try {
      setLoading(true);
      const [businessData, documentsData] = await Promise.all([
        adminService.getBusinessById(Number(businessId)),
        adminService.getBusinessKYCDocuments(Number(businessId))
      ]);
      setBusiness(businessData);
      setKycDocuments(documentsData);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch business details');
      navigate('/admin/businesses');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveBusiness = async () => {
    if (!business) return;

    try {
      setProcessing(true);
      await adminService.approveBusiness({ business_id: business.business_id });
      toast.success('Business approved successfully!');
      setApproveModal(false);
      fetchBusinessDetails();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to approve business');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectBusiness = async () => {
    if (!business || !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      setProcessing(true);
      await adminService.rejectBusiness({
        business_id: business.business_id,
        rejection_reason: rejectionReason
      });
      toast.success('Business rejected successfully!');
      setRejectModal(false);
      setRejectionReason('');
      fetchBusinessDetails();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reject business');
    } finally {
      setProcessing(false);
    }
  };

  const handleSuspendBusiness = async () => {
    if (!business || !suspensionReason.trim()) {
      toast.error('Please provide a suspension reason');
      return;
    }

    try {
      setProcessing(true);
      await adminService.suspendBusiness({
        business_id: business.business_id,
        suspension_reason: suspensionReason
      });
      toast.success('Business suspended successfully!');
      setSuspendModal(false);
      setSuspensionReason('');
      fetchBusinessDetails();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to suspend business');
    } finally {
      setProcessing(false);
    }
  };

  const handleUnsuspend = async () => {
    if (!business) return;

    try {
      await adminService.unsuspendBusiness(business.business_id);
      toast.success('Business unsuspended successfully!');
      fetchBusinessDetails();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to unsuspend business');
    }
  };

  const handleVerify = async () => {
    if (!business) return;

    try {
      await adminService.verifyBusiness(business.business_id);
      toast.success('Business verified successfully!');
      fetchBusinessDetails();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to verify business');
    }
  };

  const handleVerifyKYC = async (documentId: number, status: 'verified' | 'rejected') => {
    try {
      await adminService.verifyKYCDocument({ document_id: documentId, verification_status: status });
      toast.success(`Document ${status} successfully!`);
      fetchBusinessDetails();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to verify document');
    }
  };

  const getStatusBadge = (status: BusinessStatus) => {
    const styles = {
      pending_approval: 'bg-warning-50 text-warning-600 border-warning-200',
      approved: 'bg-success-50 text-success-600 border-success-200',
      rejected: 'bg-danger-50 text-danger-600 border-danger-200',
      active: 'bg-success-50 text-success-600 border-success-200',
      suspended: 'bg-danger-50 text-danger-600 border-danger-200',
      paused: 'bg-secondary-50 text-secondary-600 border-secondary-200'
    };
    return styles[status] || 'bg-secondary-50 text-secondary-600 border-secondary-200';
  };

  const getStatusIcon = (status: BusinessStatus) => {
    switch (status) {
      case 'pending_approval': return <Clock size={16} />;
      case 'approved': return <CheckCircle size={16} />;
      case 'rejected': return <XCircle size={16} />;
      case 'active': return <CheckCircle size={16} />;
      case 'suspended': return <Ban size={16} />;
      case 'paused': return <Archive size={16} />;
      default: return <Store size={16} />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader className="animate-spin text-primary-500" size={48} />
      </div>
    );
  }

  if (!business) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <AlertTriangle className="text-danger-500 mx-auto mb-4" size={64} />
          <h2 className="text-2xl font-bold text-secondary-900 mb-2">Business Not Found</h2>
          <p className="text-secondary-600 mb-6">The business you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/admin/businesses')}
            className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold"
          >
            Back to Businesses
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/businesses')}
            className="bg-secondary-100 hover:bg-secondary-200 text-secondary-700 p-2 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-secondary-900">Business Details</h1>
            <p className="text-secondary-600">View and manage business information</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex items-center space-x-3">
          {business.status === 'pending_approval' && (
            <>
              <button
                onClick={() => setApproveModal(true)}
                className="bg-success-500 hover:bg-success-600 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2"
              >
                <CheckCircle size={18} />
                <span>Approve</span>
              </button>
              <button
                onClick={() => setRejectModal(true)}
                className="bg-danger-500 hover:bg-danger-600 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2"
              >
                <XCircle size={18} />
                <span>Reject</span>
              </button>
            </>
          )}
          {(business.status === 'active' || business.status === 'approved') && (
            <button
              onClick={() => setSuspendModal(true)}
              className="bg-warning-500 hover:bg-warning-600 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2"
            >
              <Ban size={18} />
              <span>Suspend</span>
            </button>
          )}
          {business.status === 'suspended' && (
            <button
              onClick={handleUnsuspend}
              className="bg-success-500 hover:bg-success-600 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2"
            >
              <CheckCircle size={18} />
              <span>Unsuspend</span>
            </button>
          )}
        </div>
      </div>

      {/* Business Header Card */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <div className="flex items-start space-x-6">
          {getImageUrl(business.logo_url) ? (
            <div className="relative w-32 h-32 rounded-xl overflow-hidden border-2 border-secondary-200 bg-secondary-50 shadow-md">
              <img
                src={getImageUrl(business.logo_url) || ''}
                alt={business.business_name}
                className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const parent = e.currentTarget.parentElement;
                  if (parent) {
                    const fallback = parent.querySelector('.fallback-details-logo');
                    if (fallback) (fallback as HTMLElement).style.display = 'flex';
                  }
                }}
              />
              <div className="fallback-details-logo absolute inset-0 hidden items-center justify-center bg-primary-100">
                <Store className="text-primary-600" size={64} />
              </div>
            </div>
          ) : (
            <div className="w-32 h-32 bg-primary-100 rounded-xl flex items-center justify-center border-2 border-primary-200">
              <Store className="text-primary-600" size={64} />
            </div>
          )}

          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-3xl font-bold text-secondary-900 mb-2">{business.business_name}</h2>
                <div className="flex items-center space-x-2 mb-3">
                  <span className="text-lg text-secondary-600">{business.category_name}</span>
                  <span className="text-secondary-400">•</span>
                  <div className="flex items-center space-x-1">
                    <Star size={18} className="text-warning-500 fill-warning-500" />
                    <span className="text-lg font-semibold">{business.avg_rating}</span>
                    <span className="text-secondary-500">({business.total_reviews} reviews)</span>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className={`inline-flex items-center space-x-1 px-4 py-2 rounded-full text-sm font-semibold border-2 ${getStatusBadge(business.status)}`}>
                    {getStatusIcon(business.status)}
                    <span className="capitalize">{business.status.replace('_', ' ')}</span>
                  </span>
                  {business.is_verified === 1 && (
                    <span className="inline-flex items-center space-x-1 bg-success-50 text-success-600 px-4 py-2 rounded-full text-sm font-semibold border-2 border-success-200">
                      <Shield size={16} />
                      <span>Verified Business</span>
                    </span>
                  )}
                  {business.is_featured === 1 && (
                    <span className="inline-flex items-center space-x-1 bg-warning-50 text-warning-600 px-4 py-2 rounded-full text-sm font-semibold border-2 border-warning-200">
                      <Star size={16} />
                      <span>Featured</span>
                    </span>
                  )}
                  {business.is_24_7 === 1 && (
                    <span className="inline-flex items-center space-x-1 bg-primary-50 text-primary-600 px-4 py-2 rounded-full text-sm font-semibold border-2 border-primary-200">
                      <Clock size={16} />
                      <span>24/7 Open</span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        {business.description && (
          <div className="mt-6 pt-6 border-t">
            <h3 className="text-sm font-semibold text-secondary-900 mb-2">About Business</h3>
            <p className="text-secondary-700 leading-relaxed">{business.description}</p>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-secondary-100 p-1 rounded-lg">
        {['Business Info', 'KYC Documents', 'Business Photos', 'Business Products'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-all ${activeTab === tab
              ? 'bg-white text-primary-600 shadow-sm'
              : 'text-secondary-600 hover:text-secondary-900 hover:bg-secondary-200'
              }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {/* Tab 1: Business Info */}
        {activeTab === 'Business Info' && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-soft border-l-4 border-success-500">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-secondary-600">Transactions</p>
                  <TrendingUp size={20} className="text-success-600" />
                </div>
                <p className="text-2xl font-bold text-secondary-900">{business.total_transactions || 0}</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-soft border-l-4 border-warning-500">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-secondary-600">Average Rating</p>
                  <Star size={20} className="text-warning-600" />
                </div>
                <p className="text-2xl font-bold text-secondary-900">{business.avg_rating}</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-soft border-l-4 border-secondary-500">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-secondary-600">Total Reviews</p>
                  <FileText size={20} className="text-secondary-600" />
                </div>
                <p className="text-2xl font-bold text-secondary-900">{business.total_reviews}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Contact Information */}
              <div className="bg-white rounded-xl shadow-soft p-6">
                <h3 className="text-xl font-bold text-secondary-900 mb-4 flex items-center space-x-2">
                  <User size={20} className="text-primary-600" />
                  <span>Contact Information</span>
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <User size={18} className="text-secondary-400 mt-1" />
                    <div className="flex-1">
                      <p className="text-sm text-secondary-600 mb-1">Owner Name</p>
                      <p className="text-secondary-900 font-medium">{business.owner_name}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Phone size={18} className="text-secondary-400 mt-1" />
                    <div className="flex-1">
                      <p className="text-sm text-secondary-600 mb-1">Phone Number</p>
                      <p className="text-secondary-900 font-medium">{business.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Mail size={18} className="text-secondary-400 mt-1" />
                    <div className="flex-1">
                      <p className="text-sm text-secondary-600 mb-1">Email Address</p>
                      <p className="text-secondary-900 font-medium">{business.email}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Location Information */}
              <div className="bg-white rounded-xl shadow-soft p-6">
                <h3 className="text-xl font-bold text-secondary-900 mb-4 flex items-center space-x-2">
                  <MapPin size={20} className="text-primary-600" />
                  <span>Location</span>
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Building size={18} className="text-secondary-400 mt-1" />
                    <div className="flex-1">
                      <p className="text-sm text-secondary-600 mb-1">Address</p>
                      <p className="text-secondary-900 font-medium">{business.address}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-secondary-600 mb-1">City</p>
                      <p className="text-secondary-900 font-medium">{business.city}</p>
                    </div>
                    <div>
                      <p className="text-sm text-secondary-600 mb-1">State</p>
                      <p className="text-secondary-900 font-medium">{business.state}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-secondary-600 mb-1">Pincode</p>
                      <p className="text-secondary-900 font-medium">{business.pincode || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-secondary-600 mb-1">Country</p>
                      <p className="text-secondary-900 font-medium">{business.country}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Business Information */}
              <div className="bg-white rounded-xl shadow-soft p-6">
                <h3 className="text-xl font-bold text-secondary-900 mb-4 flex items-center space-x-2">
                  <Store size={20} className="text-primary-600" />
                  <span>Business Information</span>
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Hash size={18} className="text-secondary-400 mt-1" />
                    <div className="flex-1">
                      <p className="text-sm text-secondary-600 mb-1">Business ID</p>
                      <p className="text-secondary-900 font-medium">#{business.business_id}</p>
                    </div>
                  </div>
                  {business.gst_number && (
                    <div className="flex items-start space-x-3">
                      <FileText size={18} className="text-secondary-400 mt-1" />
                      <div className="flex-1">
                        <p className="text-sm text-secondary-600 mb-1">GST Number</p>
                        <p className="text-secondary-900 font-medium">{business.gst_number}</p>
                      </div>
                    </div>
                  )}
                  {business.license_number && (
                    <div className="flex items-start space-x-3">
                      <Shield size={18} className="text-secondary-400 mt-1" />
                      <div className="flex-1">
                        <p className="text-sm text-secondary-600 mb-1">License Number</p>
                        <p className="text-secondary-900 font-medium">{business.license_number}</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-start space-x-3">
                    <Calendar size={18} className="text-secondary-400 mt-1" />
                    <div className="flex-1">
                      <p className="text-sm text-secondary-600 mb-1">Registered On</p>
                      <p className="text-secondary-900 font-medium">{formatDate(business.created_at)}</p>
                    </div>
                  </div>
                  {business.approval_date && (
                    <div className="flex items-start space-x-3">
                      <CheckCircle size={18} className="text-success-600 mt-1" />
                      <div className="flex-1">
                        <p className="text-sm text-secondary-600 mb-1">Approved On</p>
                        <p className="text-secondary-900 font-medium">{formatDate(business.approval_date)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Details */}
              <div className="bg-white rounded-xl shadow-soft p-6">
                <h3 className="text-xl font-bold text-secondary-900 mb-4 flex items-center space-x-2">
                  <Package size={20} className="text-primary-600" />
                  <span>Additional Details</span>
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3 border-b">
                    <span className="text-secondary-600">Onboarding Completed</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${business.onboarding_completed ? 'bg-success-100 text-success-600' : 'bg-warning-100 text-warning-600'}`}>
                      {business.onboarding_completed ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b">
                    <span className="text-secondary-600">Is Paused</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${business.is_paused ? 'bg-warning-100 text-warning-600' : 'bg-success-100 text-success-600'}`}>
                      {business.is_paused ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b">
                    <span className="text-secondary-600">Latitude</span>
                    <span className="text-secondary-900 font-medium">{business.latitude || 'N/A'}</span>
                  </div>
                  <div className="flex items-center justify-between py-3">
                    <span className="text-secondary-600">Longitude</span>
                    <span className="text-secondary-900 font-medium">{business.longitude || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Rejection Reason (if rejected) */}
            {business.status === 'rejected' && business.rejection_reason && (
              <div className="bg-danger-50 border-2 border-danger-200 rounded-xl p-6">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="text-danger-600 mt-1" size={24} />
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-danger-900 mb-2">Rejection Reason</h3>
                    <p className="text-danger-700">{business.rejection_reason}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Verify Business Button */}
            {!business.is_verified && business.status === 'active' && (
              <div className="bg-primary-50 border-2 border-primary-200 rounded-xl p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Shield className="text-primary-600" size={32} />
                    <div>
                      <h3 className="text-lg font-bold text-primary-900 mb-1">Verify This Business</h3>
                      <p className="text-primary-700">Mark this business as verified to increase trust.</p>
                    </div>
                  </div>
                  <button
                    onClick={handleVerify}
                    className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold flex items-center space-x-2"
                  >
                    <Shield size={20} />
                    <span>Verify Business</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab 2: KYC Documents */}
        {activeTab === 'KYC Documents' && (
          <div className="bg-white rounded-xl shadow-soft p-6">
            <h3 className="text-xl font-bold text-secondary-900 mb-4 flex items-center space-x-2">
              <FileText size={20} className="text-primary-600" />
              <span>KYC Documents</span>
            </h3>
            {kycDocuments.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {kycDocuments.map((doc) => {
                  const docUrl = getImageUrl(doc.document_url) || '';

                  return (
                    <div key={doc.document_id} className="border-2 border-secondary-200 rounded-lg p-4 hover:border-primary-300 transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <FileText size={20} className="text-primary-600" />
                          <p className="font-semibold text-secondary-900 capitalize">{doc.document_type.replace('_', ' ')}</p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${doc.verification_status === 'verified' ? 'bg-success-50 text-success-600' :
                          doc.verification_status === 'rejected' ? 'bg-danger-50 text-danger-600' :
                            'bg-warning-50 text-warning-600'
                          }`}>
                          {doc.verification_status}
                        </span>
                      </div>
                      <p className="text-xs text-secondary-500 mb-3 truncate" title={doc.document_name}>
                        {doc.document_name}
                      </p>
                      <div className="flex items-center space-x-2">
                        <a
                          href={docUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 bg-primary-50 hover:bg-primary-100 text-primary-600 px-3 py-2 rounded text-sm font-semibold text-center transition-colors"
                        >
                          View Document
                        </a>
                        {doc.verification_status === 'pending' && (
                          <div className="flex space-x-1">
                            <button
                              onClick={() => handleVerifyKYC(doc.document_id, 'verified')}
                              className="bg-success-50 hover:bg-success-100 text-success-600 p-2 rounded transition-colors"
                              title="Verify"
                            >
                              <CheckCircle size={16} />
                            </button>
                            <button
                              onClick={() => handleVerifyKYC(doc.document_id, 'rejected')}
                              className="bg-danger-50 hover:bg-danger-100 text-danger-600 p-2 rounded transition-colors"
                              title="Reject"
                            >
                              <XCircle size={16} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-secondary-500">
                <FileText size={48} className="mx-auto mb-3 opacity-50" />
                <p>No KYC documents available for this business.</p>
              </div>
            )}
          </div>
        )}

        {/* Tab 3: Business Photos */}
        {activeTab === 'Business Photos' && (
          <div className="bg-white rounded-xl shadow-soft p-6">
            <h3 className="text-xl font-bold text-secondary-900 mb-4 flex items-center space-x-2">
              <ImageIcon size={20} className="text-primary-600" />
              <span>Business Photos</span>
            </h3>
            {business.photos && business.photos.filter(photo => !failedPhotoIds.includes(photo.photo_id)).length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {business.photos
                  .filter(photo => !failedPhotoIds.includes(photo.photo_id))
                  .map((photo) => {
                    const photoUrl = getImageUrl(photo.photo_url) || '';

                    return (
                      <div
                        key={photo.photo_id}
                        className="relative group overflow-hidden rounded-xl border border-secondary-200 shadow-sm cursor-pointer"
                        onClick={() => setPreviewImage(photoUrl)}
                      >
                        <img
                          src={photoUrl}
                          alt={photo.photo_type}
                          className="w-full h-48 object-cover transition-transform duration-350 group-hover:scale-110"
                          onError={() => {
                            setFailedPhotoIds(prev => [...prev, photo.photo_id]);
                          }}
                        />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-3">
                        <span className="text-white text-xs font-semibold uppercase tracking-wider bg-primary-600/90 self-start px-2.5 py-1 rounded-md mb-1 shadow-sm">
                          {photo.photo_type.replace('_', ' ')}
                        </span>
                        <p className="text-white/80 text-[10px]">Click to expand preview</p>
                      </div>
                      <div className="absolute bottom-3 left-3 bg-secondary-900/80 text-white text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-md group-hover:opacity-0 transition-opacity duration-300">
                        {photo.photo_type.replace('_', ' ')}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-secondary-500">
                <ImageIcon size={48} className="mx-auto mb-3 opacity-50" />
                <p>No photos available for this business.</p>
              </div>
            )}
          </div>
        )}

        {/* Tab 4: Business Products */}
        {activeTab === 'Business Products' && (
          <div className="bg-white rounded-xl shadow-soft p-6">
            <h3 className="text-xl font-bold text-secondary-900 mb-4 flex items-center space-x-2">
              <Package size={20} className="text-primary-600" />
              <span>Services & Products</span>
            </h3>
            {business.services_products && business.services_products.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {business.services_products.map((item) => {
                  const itemImageUrl = getImageUrl(item.image);

                  return (
                    <div key={item.id} className="border-2 border-secondary-200 rounded-lg p-4 hover:border-primary-300 transition-colors">
                      {itemImageUrl && (
                        <img
                          src={itemImageUrl}
                          alt={item.name}
                          className="w-full h-32 object-cover rounded-lg mb-3"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      )}
                      <h4 className="font-semibold text-secondary-900 mb-1">{item.name}</h4>
                      {item.description && (
                        <p className="text-xs text-secondary-600 mb-2 line-clamp-2">{item.description}</p>
                      )}
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-lg font-bold text-primary-600">
                          {business.country?.toLowerCase() === 'india' ? '₹' : '$'}
                          {item.price}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${item.is_available ? 'bg-success-50 text-success-600' : 'bg-danger-50 text-danger-600'
                          }`}>
                          {item.is_available ? 'Available' : 'Unavailable'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-secondary-500">
                <Package size={48} className="mx-auto mb-3 opacity-50" />
                <p>No products or services listed.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Approve Modal */}
      {approveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center space-x-3">
                <div className="bg-success-100 p-3 rounded-full">
                  <CheckCircle className="text-success-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-secondary-900">Approve Business</h2>
              </div>
            </div>
            <div className="p-6">
              <p className="text-secondary-700 mb-4">
                Are you sure you want to approve <strong>{business.business_name}</strong>?
              </p>
              <p className="text-sm text-secondary-600">
                The business owner will be notified and their business will become active on the platform.
              </p>
            </div>
            <div className="flex space-x-3 p-6 bg-secondary-50 rounded-b-2xl">
              <button
                onClick={() => setApproveModal(false)}
                disabled={processing}
                className="flex-1 px-6 py-3 bg-white border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleApproveBusiness}
                disabled={processing}
                className="flex-1 px-6 py-3 bg-success-500 hover:bg-success-600 text-white rounded-lg font-semibold flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                {processing ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    <span>Approving...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle size={20} />
                    <span>Approve</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center space-x-3">
                <div className="bg-danger-100 p-3 rounded-full">
                  <XCircle className="text-danger-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-secondary-900">Reject Business</h2>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-secondary-700">
                Rejecting <strong>{business.business_name}</strong>
              </p>
              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  Rejection Reason <span className="text-danger-500">*</span>
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  placeholder="Provide a clear reason for rejection..."
                  rows={4}
                  required
                />
              </div>
            </div>
            <div className="flex space-x-3 p-6 bg-secondary-50 rounded-b-2xl">
              <button
                onClick={() => setRejectModal(false)}
                disabled={processing}
                className="flex-1 px-6 py-3 bg-white border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectBusiness}
                disabled={processing || !rejectionReason.trim()}
                className="flex-1 px-6 py-3 bg-danger-500 hover:bg-danger-600 text-white rounded-lg font-semibold flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                {processing ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    <span>Rejecting...</span>
                  </>
                ) : (
                  <>
                    <XCircle size={20} />
                    <span>Reject</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Suspend Modal */}
      {suspendModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center space-x-3">
                <div className="bg-warning-100 p-3 rounded-full">
                  <Ban className="text-warning-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-secondary-900">Suspend Business</h2>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-secondary-700">
                Suspending <strong>{business.business_name}</strong>
              </p>
              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  Suspension Reason <span className="text-danger-500">*</span>
                </label>
                <textarea
                  value={suspensionReason}
                  onChange={(e) => setSuspensionReason(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  placeholder="Provide a clear reason for suspension..."
                  rows={4}
                  required
                />
              </div>
            </div>
            <div className="flex space-x-3 p-6 bg-secondary-50 rounded-b-2xl">
              <button
                onClick={() => setSuspendModal(false)}
                disabled={processing}
                className="flex-1 px-6 py-3 bg-white border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSuspendBusiness}
                disabled={processing || !suspensionReason.trim()}
                className="flex-1 px-6 py-3 bg-warning-500 hover:bg-warning-600 text-white rounded-lg font-semibold flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                {processing ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    <span>Suspending...</span>
                  </>
                ) : (
                  <>
                    <Ban size={20} />
                    <span>Suspend</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-[100] p-4 cursor-zoom-out"
          onClick={() => setPreviewImage(null)}
        >
          {/* Close Button - Fixed to top right of screen */}
          <button
            onClick={() => setPreviewImage(null)}
            className="fixed top-6 right-6 text-white text-opacity-80 hover:text-opacity-100 hover:scale-110 transition-all z-[110] bg-black bg-opacity-50 rounded-full p-2"
            aria-label="Close preview"
          >
            <XCircle size={40} strokeWidth={1.5} />
          </button>

          <div className="relative max-w-7xl w-full h-full flex items-center justify-center p-4">
            <img
              src={previewImage}
              alt="Preview"
              className="max-h-[90vh] max-w-full rounded-lg object-contain shadow-2xl cursor-default"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessDetails;

